import { type defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import {
  type BrokenDatabase,
  type Database,
  type DatabaseField,
  type EntryCreateOptions,
  type EntryWithContext,
} from '@databases/types/database';
import { type DatabaseView } from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import { createEntry, isNonBrokenDatabase } from '@databases/utils/database/utils';

import { getExperimentValue } from '@canvas/client-feature-flags';

import { isCreate } from '../entryPicker/DatabaseEntryPicker';

import { handleCreateEntry } from './createEntry';

enum MacroType {
  DATABASE = 'DATABASE',
  ENTRY = 'ENTRY',
  VALUE = 'VALUE',
}

export const getEmbedUrl = async (
  selectedDatabase: Database | BrokenDatabase,
  selectedEntry: EntryWithContext | EntryCreateOptions | undefined,
  selectedField: DatabaseField | undefined,
  dispatch: ReturnType<typeof defaultStoreUtils.useDispatch>,
  store: any,
  view: DatabaseView | null,
  analyticsClient: any,
  spaceKey: string,
  baseUrl: string
) => {
  if (isNonBrokenDatabase(selectedDatabase)) {
    let queryString = '';
    let macroType: MacroType = MacroType.DATABASE;
    let isNew = false;

    if (selectedEntry) {
      if (isCreate(selectedEntry)) {
        let createdEntryId;
        const removeAddEntryPendingLogic = getExperimentValue(
          'removeAddEntryPending',
          'enabled',
          {
            fireExperimentExposure: true,
          }
        );
        if (removeAddEntryPendingLogic) {
          const entry = createEntry(selectedDatabase);
          dispatch(
            addChangeToDraft(selectedDatabase.contentId, 'main', {
              type: DatabaseChangeType.ADD_ENTRY,
              entry,
            })
          );
          createdEntryId = entry.id;
        } else {
          createdEntryId = await handleCreateEntry(selectedDatabase, dispatch);
        }
        if (createdEntryId) {
          queryString = `?entryId=${createdEntryId}`;
          isNew = true;
        }
      } else {
        queryString = `?entryId=${selectedEntry.entry.id}`;
      }
      macroType = MacroType.ENTRY;
    }

    if (selectedField) {
      queryString += `&fieldId=${selectedField.id}`;
      macroType = MacroType.VALUE;
    }

    if (!selectedEntry && !selectedField && view) {
      queryString += `?savedViewId=${view.id}`;
    }

    await analyticsClient.sendMacroCreatedEvent({
      macroType: macroType,
      contentId: selectedDatabase.contentId,
      isNew: isNew,
      source: 'databaseReferenceModal',
    });

    const message: { finalDatabaseUrl: string } = {
      finalDatabaseUrl: `${baseUrl}/spaces/${spaceKey}/database/${selectedDatabase.contentId}${queryString}`,
    };

    return message;
  }
};
